import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";
import { ProductListItem } from "@/services/productsApi";
import { useCart } from "@/contexts/CartContext";

interface ProductCardProps {
  product: ProductListItem;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const { addToCart } = useCart();

  const handleAddToCart = () => {
    addToCart({
      id: product.id.toString(),
      name: product.name,
      price: product.price,
      image: product.image_base64 ? `data:image/jpeg;base64,${product.image_base64}` : undefined,
      sku: product.sku
    }, 1);
  };

  const productUrl = product.slug ? `/produit/${product.slug}` : `/produit/${product.id}`;

  return (
    <Card className="group h-full overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
      <CardContent className="p-0">
        <div className="relative aspect-square overflow-hidden">
          {product.image_base64 ? (
            <img
              src={`data:image/jpeg;base64,${product.image_base64}`}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-muted-foreground">Pas d'image</span>
            </div>
          )}
          {product.brand && (
            <Badge 
              variant="secondary" 
              className="absolute top-2 left-2 bg-background/90 backdrop-blur"
            >
              {product.brand}
            </Badge>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="font-semibold text-sm line-clamp-2 mb-2 group-hover:text-primary transition-colors">
            <a href={productUrl} className="hover:underline">
              {product.name}
            </a>
          </h3>
          
          {product.short_description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {product.short_description}
            </p>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="font-bold text-primary">
                {product.price.toFixed(2)} {product.currency}
              </span>
              {product.sku && (
                <span className="text-xs text-muted-foreground">
                  Réf: {product.sku}
                </span>
              )}
            </div>
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddToCart}
              className="shrink-0"
            >
              <ShoppingCart className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};